import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './OrganizerAuth.css';

const OrganizerRegister = () => {
  const [personas, setPersonas] = useState([]);
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company_name: '',
    password: '',
    confirmPassword: '',
    persona: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadPersonas();
  }, []);

  const loadPersonas = async () => {
    try {
      const response = await fetch('/api/organizer/personas', {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setPersonas(data);
      }
    } catch (err) {
      console.error('Failed to load personas:', err);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError(''); // Clear error on input change
  };

  const validateStep = (step) => {
    setError('');
    if (step === 1) {
      if (!formData.name.trim()) {
        setError('Name is required');
        return false;
      }
      if (!formData.email.trim()) {
        setError('Email is required');
        return false;
      }
      if (!formData.email.includes('@')) {
        setError('Please enter a valid email');
        return false;
      }
    } else if (step === 2) {
      if (!formData.company_name.trim()) {
        setError('Company name is required');
        return false;
      }
      if (!formData.persona) {
        setError('Please select a persona');
        return false;
      }
    } else if (step === 3) {
      if (formData.password.length < 6) {
        setError('Password must be at least 6 characters');
        return false;
      }
      if (formData.password !== formData.confirmPassword) {
        setError('Passwords do not match');
        return false;
      }
    }
    return true;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    setError('');
    setCurrentStep(currentStep - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateStep(3)) {
      return;
    }

    setLoading(true);

    try {
      const response = await fetch('/api/organizer/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          company_name: formData.company_name,
          password: formData.password,
          persona: formData.persona,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        // Store organizer name in localStorage
        localStorage.setItem('organizer_name', formData.name);
        
        // Navigate to onboarding voice assistant instead of login
        navigate('/organizer/onboarding', { 
          state: { 
            organizerId: data.organizer_id,
            persona: formData.persona,
            organizerName: formData.name
          } 
        });
      } else {
        setError(data.error || 'Registration failed');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const stepTitles = ['Personal Information', 'Company Details', 'Security'];
  
  return (
    <div className="organizer-auth-container">
      <h1 className="organizer-auth-header">personaX</h1>
      
      <div className="auth-main-content">
        <div className="organizer-auth-card">
          <h1>Create Account</h1>
          <p className="subtitle">{stepTitles[currentStep - 1]}</p>
          
          {/* Step Indicators */}
          <div className="step-indicators">
            {[1, 2, 3].map((step) => (
              <div key={step} className={`step-indicator ${currentStep === step ? 'active' : ''} ${currentStep > step ? 'completed' : ''}`}>
                <div className="step-dot">{step}</div>
                {step < 3 && <div className="step-line"></div>}
              </div>
            ))}
          </div>
          
          {error && <div className="error-message">{error}</div>}
          
          <form onSubmit={handleSubmit}>
            <div className="form-steps-container">
              {/* Step 1: Name and Email */}
              <div 
                className={`form-step ${currentStep === 1 ? 'active' : ''}`}
                tabIndex={currentStep === 1 ? 0 : -1}
              >
                <div className="form-group">
                  <label>Full Name</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="John Doe"
                    tabIndex={currentStep === 1 ? 0 : -1}
                  />
                </div>
                
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="your@email.com"
                    tabIndex={currentStep === 1 ? 0 : -1}
                  />
                </div>
              </div>

              {/* Step 2: Company Name and Persona */}
              <div 
                className={`form-step ${currentStep === 2 ? 'active' : ''}`}
                tabIndex={currentStep === 2 ? 0 : -1}
              >
                <div className="form-group">
                  <label>Company Name</label>
                  <input
                    type="text"
                    name="company_name"
                    value={formData.company_name}
                    onChange={handleChange}
                    required
                    placeholder="Your Company Inc."
                    tabIndex={currentStep === 2 ? 0 : -1}
                  />
                </div>

                <div className="form-group">
                  <label>Interviewer Persona *</label>
                  <select
                    name="persona"
                    value={formData.persona}
                    onChange={handleChange}
                    required
                    tabIndex={currentStep === 2 ? 0 : -1}
                  >
                    <option value="">Select a persona</option>
                    {personas.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name} - {p.description}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Step 3: Password and Confirm Password */}
              <div 
                className={`form-step ${currentStep === 3 ? 'active' : ''}`}
                tabIndex={currentStep === 3 ? 0 : -1}
              >
                <div className="form-group">
                  <label>Password</label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    placeholder="Minimum 6 characters"
                    tabIndex={currentStep === 3 ? 0 : -1}
                  />
                </div>
                
                <div className="form-group">
                  <label>Confirm Password</label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    required
                    placeholder="Re-enter password"
                    tabIndex={currentStep === 3 ? 0 : -1}
                  />
                </div>
              </div>
            </div>
            
            {/* Navigation Buttons */}
            <div className="form-navigation">
              {currentStep > 1 && (
                <button
                  type="button"
                  onClick={handlePrevious}
                  className="nav-btn prev-btn"
                >
                  Previous
                </button>
              )}
              {currentStep < 3 ? (
                <button
                  type="button"
                  onClick={handleNext}
                  className="nav-btn next-btn"
                >
                  Next
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={loading}
                  className="nav-btn submit-btn"
                >
                  {loading ? 'Creating account...' : 'Register'}
                </button>
              )}
            </div>
          </form>
          
          <p className="auth-footer">
            Already have an account?{' '}
            <a href="/organizer/login">Login here</a>
          </p>
        </div>

        <div className="auth-image-container">
          <img src="/loginAII.png" alt="AI Interview Illustration" />
        </div>
      </div>
    </div>
  );
};

export default OrganizerRegister;

